import React, { useEffect, useState } from 'react';
import { configureStore } from '@reduxjs/toolkit';
import PropTypes from 'prop-types';
import { createMicroFrontend } from '@zillow/frontier';
import { Provider } from 'react-redux';
import { AD_SLOTS } from '@zillow/ad-state-machine';
import { MstSentry } from '@zillow/mst-sentry';
import Observer from '@researchgate/react-intersection-observer';

import { sentryScope, captureException } from './util/tracking';
import { AdStateParentContainer } from './components/srp/state-ads/AdStateParentContainer';
import NavAdContainer from './components/srp/legacy-ads/NavAdContainer';
import DisplayAdContainer from './components/srp/legacy-ads/DisplayAdContainer';
import { getAdSlotLazyLoadDistance, getSrpPageVariant } from './components/srp/util/common';
import { isRentalOnlyChecked } from './components/srp/util/getFromState';

const UPDATE_STATE = 'UPDATE_STATE';
let store = null;
const shadowReducer = (state, action) => {
    if (action.type === UPDATE_STATE) {
        return action.state;
    }
    return state;
};
const MstSearchAdCmp = ({ srpState, adSlot, onSlotRenderEnded, isTall, isIABAd }) => {
    if (store === null) {
        store = configureStore({
            reducer: shadowReducer,
            preloadedState: srpState,
        });
    }
    const pageVariant = getSrpPageVariant(srpState);
    const [hasBeenVisible, setHasBeenVisible] = useState(false);
    const [isRentalSearch, setIsRentalSearch] = useState(isRentalOnlyChecked(srpState));

    // Keep our store in sync with state from SRP
    useEffect(() => {
        setIsRentalSearch(isRentalOnlyChecked(srpState));
        store.dispatch({ type: UPDATE_STATE, state: srpState });
    }, [srpState]);

    // whenever the ad slot or pageVariant changes we need to reset the hasBeenVisible flag
    // we also will send the isEmpty flag to onSlotRenderEnded so that the trigger point appears again.
    useEffect(() => {
        setHasBeenVisible(false);
        if (adSlot === AD_SLOTS.NAV2 && onSlotRenderEnded) {
            onSlotRenderEnded({ isEmpty: false, isError: false, size: [0] });
            // The state machine sets hidAd true when showAd is false and this was causing some behavor where
            // FSSRP search_2 would never requests (since it and it's trigger point were hidden)
            onSlotRenderEnded({ hideAd: false });
        }
    }, [pageVariant, adSlot, onSlotRenderEnded]);

    const lazyLoadDistance = getAdSlotLazyLoadDistance(adSlot);
    let ad = null;
    if (adSlot === AD_SLOTS.NAV) {
        ad = (
            <NavAdContainer
                isIABAd={isIABAd}
                onSlotRenderEnded={onSlotRenderEnded}
                isTall={isTall}
                hideLoadingSpinner={false}
                containerId="nav-ad-container"
                hasBeenVisible={lazyLoadDistance ? hasBeenVisible : true}
                adSlot={adSlot}
            />
        );
    } else if (adSlot === AD_SLOTS.RENTAL) {
        ad = (
            <NavAdContainer
                isIABAd={isIABAd}
                onSlotRenderEnded={onSlotRenderEnded}
                isTall={isTall}
                hideLoadingSpinner={false}
                containerId="nav-ad-container-two"
                hasBeenVisible={lazyLoadDistance ? hasBeenVisible : true}
                adSlot={adSlot}
            />
        );
    } else if (adSlot === AD_SLOTS.DISPLAY) {
        ad = <DisplayAdContainer hasBeenVisible={lazyLoadDistance ? hasBeenVisible : true} />;
    } else if (adSlot === AD_SLOTS.NAV2) {
        ad = (
            <NavAdContainer
                isIABAd={isIABAd}
                onSlotRenderEnded={onSlotRenderEnded}
                isTall={isTall}
                hideLoadingSpinner={false}
                containerId="nav-ad-container-two"
                hasBeenVisible={hasBeenVisible}
                adSlot={adSlot}
            />
        );
    } else {
        if (typeof onSlotRenderEnded === 'function') {
            onSlotRenderEnded({ isEmpty: true });
        }
        return null;
    }

    // removing display and rental ads from state machine trial is temporary, until
    // we can go in and change the refresh rules for those two slots
    if (
        srpState.abTrials.SRP_MEDIASOLUTIONS_STATEMACHINE === 'ON' &&
        adSlot !== AD_SLOTS.DISPLAY &&
        !isRentalSearch
    ) {
        ad = (
            <AdStateParentContainer
                adSlot={adSlot}
                onSlotRenderEndedSlot={onSlotRenderEnded}
                isTall={isTall}
                isIABAd={isIABAd}
                hasBeenVisible={lazyLoadDistance ? hasBeenVisible : true}
            />
        );
    }

    if (lazyLoadDistance) {
        ad = (
            <span>
                <Observer
                    onChange={(event, stopTracking) => {
                        if (event.isIntersecting) {
                            setHasBeenVisible(true);
                            stopTracking();
                        }
                    }}
                    threshold={0}
                    disabled={hasBeenVisible}
                >
                    <span style={{ position: 'absolute', top: lazyLoadDistance }} />
                </Observer>
                {ad}
            </span>
        );
    }

    return <Provider store={store}>{ad}</Provider>;
};

MstSearchAdCmp.propTypes = {
    srpState: PropTypes.object,
    adSlot: PropTypes.string,
};

export const MstSearchAdCmpWithErrorBoundary = props => (
    <MstSentry sentryScope={sentryScope}>
        <MstSearchAdCmp {...props} />
    </MstSentry>
);

const MstSearchAd = createMicroFrontend({
    Component: MstSearchAdCmpWithErrorBoundary,
    onMicroFrontendLoad() {
        // Loading success
    },
    onMicroFrontendLoadError(e) {
        captureException(e);
    },

    fetchData: async () => {},
    getGraphQLFragments: () => {},
});

export default MstSearchAd;
